import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, TagDefinition } from '../../models/index';
import { groupIdentifierSeparator, Helper } from '../../common/';
import {
    IUserService,
    ITagsFilteringService,
    IAuthenticationService,
    ILoggerService
} from '../../services/';

export interface Pattern {
    propertyName: string;
    label: string;
    placeholder: string;
    model: string;
}

export interface SearchEnable {
    identifier?: boolean;
    tag?: boolean;
    group?: boolean;
    unit?: boolean;
    variable?: boolean;
    source?: boolean;
    groupType?: boolean;
    comment?: boolean;
    user?: boolean;
    severity?: boolean;
    ufk?: boolean;
    query?: boolean;
}

export interface SearchFormat<T> {
    identifier?: (d: T) => string;
    tag?: (d: T) => string;
    comment?: (d: T) => string;
    source?: (d: T) => string;
    group?: (d: T) => string;
    groupType?: (d: T) => string;
    unit?: (d: T) => string;
    variable?: (d: T) => string;
    name?: (d: T) => string;
    error?: (d: T) => string;
    user?: (d: T) => string;
    severity?: (d: T) => string;
    order?: any;
    ufk?: (d: T) => string;
    query?: (d: T) => string;
}

export interface Search {
    name: string;
    n: string;
    t: string;
    //i: string;
    g: string;
    c: string;
    v: string;
    so: string;
    u: string;
    err: string;
    s: string;
    m: string;
    a: string;
    ms: string;
    gt: string;
    type: string;
    unused: string;
    ufk: string;
    q: string;
}

class WtSearchBar {
    conditions: { keyword: string; callback: (arg: any, regexp: RegExp) => boolean }[];
    defaultEnable: any;
    emptySearch: any;
    enable: any;
    enabled: any;
    filteredItems: TagDefinition[];
    format: SearchFormat<any>;
    isWarning: boolean;
    items: TagDefinition[];
    onEnter;
    patterns: Pattern[];
    placeholder: string;
    savedName: string;
    savedSearches: any;
    search: Search;
    searchContext: string;
    searchId: string;
    searchString: string;
    warningTitle: string;
    isDisabled: boolean;

    private _currentUser: string;

    static $inject = [
        '$scope',
        '$rootScope',
        '$translate',
        '$timeout',
        'tagsFilteringService',
        'userService',
        'authenticationService',
        'loggerService'
    ];

    constructor(
        private $scope: angular.IScope,
        private $rootScope: angular.IRootScopeService,
        private $translate: angular.translate.ITranslateService,
        private $timeout: angular.ITimeoutService,
        private tagsFilteringService: ITagsFilteringService,
        private userService: IUserService,
        private authenticationService: IAuthenticationService,
        private loggerService: ILoggerService
    ) {
        this.searchId = Helper.getGuid();
    }

    $onInit() {
        this.defaultEnable = {
            group: true,
            unit: true,
            comment: true,
            variable: true,
            source: true,
            groupType: false,
            err: false,
            message: false,
            user: false,
            severity: false,
            app: false,
            tag: false,
            ms: false,
            ne: false,
            all: true,
            ufk: false,
            query: false
        };

        this.emptySearch = {
            name: '',
            n: '',
            t: '',
            i: '',
            g: '',
            c: '',
            v: '',
            so: '',
            u: '',
            err: '',
            s: '',
            m: '',
            a: '',
            ms: '',
            gt: '',
            unused: '',
            ne: '', //ne: not existing
            all: '',
            q: ''
        };
        //this.tagsFilteringService.format = this.format;
        this.search = angular.copy(this.emptySearch);
        this.enabled = this.mergeWithDefaultEnabled(this.enable);

        this._currentUser = this.authenticationService.authentication.userName;
        this.userService.getSavedSearchForUser(this._currentUser, this.searchContext)
            .then(list =>
                this.savedSearches = list
            );

        this.$scope.$watch(
            () => this.searchString,
            () => this.searchStringUpdated()
        );

        this.$scope.$watchCollection(
            () => this.items,
            () => {
                this.filteredItems = this.tagsFilteringService
                    .filterResults(this.items, this.searchString, groupIdentifierSeparator, this.format);
            }
        );

        this.$scope.$watch(() => this.enable, () => {
            this.enabled = this.mergeWithDefaultEnabled(this.enable);
        });

        if (!this.searchString) {
            this.searchString = this.tagsFilteringService.getActiveFilter(this.searchContext);
        }

        this.patterns = [
            {
                propertyName: 'tag',
                label: 'Tag name',
                placeholder: 'Name of the tag',
                model: 't'
            }, {
                propertyName: 'name',
                label: 'Name',
                placeholder: 'Search pattern on name',
                model: 'n'
            }, {
                propertyName: 'identifier',
                label: 'Identifier name',
                placeholder: 'Name of the identifier',
                model: 'i'
            }, {
                propertyName: 'group',
                label: 'Group',
                placeholder: 'Search pattern on group',
                model: 'g'
            }, {
                propertyName: 'query',
                label: 'Query name',
                placeholder: 'Name of the query',
                model: 'q'
            }, {
                propertyName: 'groupType',
                label: 'Group types',
                placeholder: 'Search pattern on group type',
                model: 'gt'
            }, {
                propertyName: 'unit',
                label: 'Unit',
                placeholder: 'Search pattern on unit (e.g. bar, °C)',
                model: 'u'
            }, {
                propertyName: 'user',
                label: 'User',
                placeholder: 'Search pattern on user name',
                model: 'u'
            }, {
                propertyName: 'comment',
                label: 'Comment',
                placeholder: 'Search pattern on comment',
                model: 'c'
            }, {
                propertyName: 'variable',
                label: 'Variable',
                placeholder: 'Search pattern on variable type (e.g. pressure, temperature)',
                model: 'v'
            }, {
                propertyName: 'source',
                label: 'Source',
                placeholder: 'Search pattern on connection point name',
                model: 'so'
            }, {
                propertyName: 'err',
                label: 'Error',
                placeholder: 'Search pattern on error type (e.g. missing, scatter)',
                model: 'err'
            }, {
                propertyName: 'message',
                label: 'Message',
                placeholder: 'Search pattern on message',
                model: 'm'
            }, {
                propertyName: 'severity',
                label: 'Severity',
                placeholder: 'Search pattern on severity (e.g. Error, Warning)',
                model: 's'
            }, {
                propertyName: 'app',
                label: 'Application',
                placeholder: 'Search pattern on application name',
                model: 'a'
            }, {
                propertyName: 'ufk',
                label: 'User field',
                model: 'ufk',
                placeholder: 'Search pattern on user field key (e.g. BWART=42_AND_UNIT=KG_OR_MATNR=ABC )',
                //isMatch: () => true,
            }
        ];

        this.searchStringUpdated();
    }

    private searchStringUpdated() {
        this.checkSearchString();
        this.filteredItems = this.tagsFilteringService.filterResults(this.items, this.searchString,
            groupIdentifierSeparator, this.format);
        this.tagsFilteringService.setActiveFilter(this.searchString, this.searchContext);
        this.$rootScope.$broadcast('filter', { searchString: this.searchString });
    }

    clear(e: angular.IAngularEvent): void {
        this.searchString = '';
        e.stopPropagation();
    }

    stopPropagation($event: angular.IAngularEvent) {
        const classes = ($event as any).target.className + ($event as any).target.parentElement.className;

        if (classes.indexOf('dropdown-close') <= 0) {
            $event.stopPropagation();
        }
    }

    watchKeyPress($event) {
        // not the best since names can change on view, but simplify a lot the logic
        if ($event.keyCode === 13) {
            const timer = this.$timeout(() => {
                $('#validSearch' + this.searchId).trigger('click');
                if (this.onEnter) {
                    this.onEnter();
                }
                this.$timeout.cancel(timer);
            });
        }
        if ($event.keyCode === 27) {
            const timer = this.$timeout(() => {
                $('#cancelSearch' + this.searchId).trigger('click');
                this.$timeout.cancel(timer);
            });
        }
    }

    computeSearch() {
        this.searchString = this.convertToSearchString();
    }

    refreshSearch($event: angular.IAngularEvent) {
        this.search = angular.copy(this.emptySearch);
        const classes = ($event as any).target.parentElement.className +
            ($event as any).target.parentElement.parentElement.className;
        // classes are refreshed after the event so the test must be inverted
        if (classes.indexOf('open') <= 0 && this.searchString) {
            let copySearch = this.searchString;
            this.forEachKeyword(key => {
                let pos = this.searchString.indexOf(`${key}:`);
                if (pos > -1 && (pos === 0 || this.searchString[pos - 1] === ' ')) {
                    pos += key.length + 1;
                    const value = this.searchString.substring(pos).split(' ')[0];
                    this.search[key] = value;
                    copySearch = copySearch.replace(`${key}:${value}`, '');
                }
            });
            this.search.name = copySearch.trim();
        }
    }

    convertToSearchString(): string {
        let search = this.search.name + ' ';
        this.forEachKeyword((key) => {
            if (this.search[key] && (this.search[key] as string).length > 0 && key !== 'name') {
                search += `${key}:${this.search[key]} `;
            }
        });
        return search.trim();
    }

    save(name: string) {
        this.userService.addSavedSearchForUser(this._currentUser, this.search, name, this.searchContext)
            .then(() => {
                this.savedSearches[name] = this.search;
                this.loggerService.logSuccess(this.$translate.instant('Search successfully saved.'));
                this.computeSearch();
                this.savedName = '';
            })
            .catch(reason => {
                this.loggerService.logError(this.$translate.instant('Failed to save search.'));
                this.loggerService.logError(reason);
            });
    }

    selectSearch(search) {
        this.search = search;
    }

    deleteSearch(name: string) {
        this.userService.delSavedSearchForUser(this._currentUser, name, this.searchContext)
            .then(() => {
                delete this.savedSearches[name];
            })
            .catch(reason => {
                this.loggerService.logError(this.$translate.instant('Failed to delete search.'));
                this.loggerService.logError(reason);
            });
    }

    getTitle() {
        return (this.isWarning) ? this.warningTitle : this.placeholder;
    }

    checkSearchString(): void {
        const warningKeys = [];
        this.isWarning = false;
        const allEnabled = this.completeEnabled(this.enabled);

        if (!this.searchString) {
            return;
        }

        _.forEach(this.searchString.split(' '), token => {
            const value = token.split(':');
            if (value.length > 1) {
                if (!allEnabled[value[0]]) {
                    this.isWarning = true;
                    warningKeys.push(value[0] as any);
                }
            }
        });

        if (this.isWarning) {
            this.warningTitle = this.$translate
                .instant('Invalid keyword(s) in this context : _invalidKeyList_',
                    { 'invalidKeyList': warningKeys.join(', ') });
        }
    }

    private forEachKeyword(method: (key: string) => void) {
        for (const key of Object.keys(this.search)) {
            if (this.search.hasOwnProperty(key)) {
                method(key);
            }
        }
    }

    private mergeWithDefaultEnabled(enable) {
        const mergeEnabled = angular.copy(this.defaultEnable);
        if (enable) {
            for (const p of Object.keys(enable)) {
                if (enable.hasOwnProperty(p)) {
                    mergeEnabled[p] = enable[p];
                }
            }
        }
        return mergeEnabled;
    }

    private completeEnabled(e): any {
        const all = angular.copy(e);

        all['n'] = e['name'];
        all['g'] = e['group'];
        all['i'] = e['identifier'];
        all['t'] = e['tag'];
        all['v'] = e['variable'];
        all['u'] = e['unit'] || e['user'];
        all['c'] = e['comment'];
        all['so'] = e['source'];
        all['error'] = e['err'];
        all['s'] = e['severity'];
        all['m'] = e['message'];
        all['a'] = e['app'];
        all['application'] = e['app'];
        all['gt'] = e['groupType'];
        all['q'] = e['query'];

        return all;
    }
}

export const wtSearchBar: ComponentDefinition = {
    componentId: 'wtSearchBar',
    template: require('./searchBar.component.html'),
    controller: WtSearchBar,
    controllerAs: 'vm',
    bindings: {
        classed: '=',
        conditions: '=',
        disableFavorite: '=',
        enable: '=',
        filteredItems: '=?',
        format: '=',
        items: '=',
        onEnter: '&',
        placeholder: '@',
        searchContext: '@',
        searchString: '=?',
        isDisabled: '='
    }
};